import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Loader2, Send } from "lucide-react";
import { useAppSelector } from "@/store/hooks";
import type { Wallet } from "@/interfaces/wallet";
import { makeRequest } from "@/utils/makeRequest";
import {
  formatEvmHumanBalance,
  normalizeCurrency,
  removeTrailingToken,
} from "@/utils";
import { useTransactionQuote } from "@/hooks/useTransactionQuote";
import type { WalletAsset } from "@/interfaces/asset";
import { toast } from "sonner";
import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";
import { UserVerificationDialog } from "@/components/user-component/UserVerificationDialog";
import TransactionSuccessDialog from "../TransactionSuccessDialog";
import { getErrorMessage } from "@/utils/getErrorMessage";
import WalletSummaryCard from "../swap/WalletSummaryCard";
import SelectedAssetCard from "../swap/SelectedAssetCard";
import AssetSelector from "../swap/AssetSelector";
import { getAvailableBalance, safeNumber } from "../swap/helpers";
import { KycStatus } from "@/interfaces/kyc";
import { useNavigate } from "react-router-dom";
import { useProfile } from "@/hooks";
import { UserRole } from "@/interfaces/profile";

interface TransferPayload {
  assetId: string;
  networkId: string;
  to: string;
  amount: string;
  otp: string;
  securityCode: string;
}

interface Props {
  wallet?: Wallet;
  onTransfer: (payload: TransferPayload) => Promise<void>;
  isLoading: boolean;
}

const TransferContainer = ({ wallet, onTransfer, isLoading }: Props) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<any | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const [transferAmount, setTransferAmount] = useState("");

  const [activeAsset, setActiveAsset] = useState<WalletAsset>();

  const [verificationOpen, setVerificationOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  const selectingRecipientRef = useRef(false);

  const [successData, setSuccessData] = useState<{
    asset: string;
    recipient: string;
  } | null>(null);

  const availableBalance = getAvailableBalance(activeAsset);

  const { data: profile } = useProfile(userId);

  const quoteInput = useMemo(() => {
    if (!activeAsset || !transferAmount) return null;

    const amount = Number(transferAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }

    const currency = normalizeCurrency(activeAsset?.currency ?? "");

    if (!currency) return null;

    return {
      domain: "transfer",
      amount,
      fromCurrency: currency,
      assetId: activeAsset?.id,
    };
  }, [activeAsset, transferAmount]);

  const { quote, loading } = useTransactionQuote(quoteInput, !!quoteInput);

  const transferValue = safeNumber(transferAmount);
  const totalDebitAmount = safeNumber(quote?.settlement?.totalDebit);

  const normalizedCurrency = normalizeCurrency(activeAsset?.currency ?? "");

  const isInsufficient = quote
    ? totalDebitAmount > availableBalance
    : transferValue > availableBalance;

  /**
   * Minimum transfer amount returned by the transaction quote/settings.
   *
   * We do not hardcode the minimum here so that frontend validation
   * follows the backend/admin transaction settings.
   */
  const minimumTransferAmount = 1;
  // const minimumTransferAmount = safeNumber(quote?.minimumAmount);

  useEffect(() => {
    if (availableBalance <= 0) {
      // setTransferAmount("");
    }
  }, [activeAsset?.id, availableBalance]);

  useEffect(() => {
    setSelectedRecipient(null);
    setQuery("");
    setResults([]);
  }, [activeAsset?.id]);

  useEffect(() => {
    if (selectingRecipientRef.current) {
      return;
    }

    const value = query.trim();

    if (!value || value.length < 3) {
      setResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        setIsSearching(true);

        const res = await makeRequest.get(`/user/resolve`, {
          params: { q: value },
        });

        // Don't overwrite the selected recipient's state
        // if the user selected someone while this request was running.
        if (!selectingRecipientRef.current) {
          setResults(res.data || []);
        }
      } catch {
        if (!selectingRecipientRef.current) {
          setResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [query]);

  /**
   * Recipient validation.
   *
   * We don't show an error while the user is actively searching.
   * Once searching finishes, the user must select a valid recipient.
   */
  const recipientError = useMemo(() => {
    const value = query.trim();

    if (!value) {
      return "Please enter a recipient email, UID or wallet address.";
    }

    if (isSearching) {
      return null;
    }

    if (!selectedRecipient) {
      return "Please select a valid recipient from the search results.";
    }

    return null;
  }, [query, isSearching, selectedRecipient]);

  /**
   * Amount validation.
   */
  const amountError = useMemo(() => {
    if (!transferAmount.trim()) {
      return "Please enter a transfer amount.";
    }

    if (!Number.isFinite(transferValue) || transferValue <= 0) {
      return "Transfer amount must be greater than 0.";
    }

    if (
      minimumTransferAmount > 0 &&
      Number(quote?.usdEquivalent) < minimumTransferAmount
    ) {
      return `Minimum transfer amount is ${minimumTransferAmount} USDT equivalent.`;
    }

    if (quote && totalDebitAmount > availableBalance) {
      return `Insufficient balance. ${totalDebitAmount.toFixed(
        2,
      )} ${normalizedCurrency} required including fee.`;
    }

    return null;
  }, [
    transferAmount,
    transferValue,
    minimumTransferAmount,
    normalizedCurrency,
    quote,
    totalDebitAmount,
    availableBalance,
  ]);

  const validationError = useMemo(() => {
    if (!activeAsset) {
      return "Please select an asset.";
    }

    if (recipientError) {
      return recipientError;
    }

    if (amountError) {
      return amountError;
    }

    if (!quote) {
      return "Unable to calculate the transfer fee. Please check the amount and try again.";
    }

    if (!Number.isFinite(totalDebitAmount) || totalDebitAmount <= 0) {
      return "Unable to calculate the total transfer amount.";
    }

    return null;
  }, [activeAsset, recipientError, amountError, quote, totalDebitAmount]);

  /**
   * Determines whether each individual input should display
   * an error state.
   */
  const recipientInputInvalid =
    !isSearching && query.trim().length > 0 && !selectedRecipient;

  const amountInputInvalid = transferAmount.trim().length > 0 && !!amountError;

  /**
   * The Transfer button can only proceed when every required
   * condition has passed.
   */
  const canContinue =
    !!activeAsset &&
    !!selectedRecipient &&
    !!transferAmount &&
    Number.isFinite(transferValue) &&
    transferValue > 0 &&
    !!quote &&
    Number.isFinite(totalDebitAmount) &&
    totalDebitAmount > 0 &&
    !isInsufficient &&
    !amountError &&
    !loading &&
    !isLoading;

  const handleClick = async () => {
    if (
      profile?.role !== UserRole.SUPER &&
      profile?.role !== UserRole.ADMIN &&
      profile?.kycStatus !== KycStatus.APPROVED
    ) {
      navigate("/dashboard/kyc");
      return;
    }

    if (!activeAsset) {
      toast.error("Please select an asset.");
      return;
    }

    if (!selectedRecipient) {
      toast.error("Please select a valid recipient.");
      return;
    }

    if (!transferAmount) {
      toast.error("Please enter a transfer amount.");
      return;
    }

    if (!quote) {
      toast.error("Unable to calculate the transfer fee. Please try again.");
      return;
    }

    if (totalDebitAmount > availableBalance) {
      toast.warning(
        `Insufficient balance. ${totalDebitAmount.toFixed(
          2,
        )} ${normalizedCurrency} required including fee.`,
      );
      return;
    }

    setVerificationOpen(true);
  };

  const executeTransfer = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    if (!activeAsset || !selectedRecipient) {
      toast.error("Please select an asset and recipient.");
      return;
    }

    try {
      await onTransfer({
        assetId: activeAsset.id,
        networkId: activeAsset.networkId,
        to: selectedRecipient.uid,
        amount: transferAmount,
        otp,
        securityCode,
      });

      setSuccessData({
        asset: activeAsset?.token?.symbol ?? "",
        recipient:
          selectedRecipient?.name ??
          selectedRecipient?.email ??
          selectedRecipient?.uid ??
          "",
      });

      setSuccessOpen(true);

      setTransferAmount("");
      setQuery("");
      setSelectedRecipient(null);
      setActiveAsset(undefined);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div>
      <div data-lenis-prevent className="w-full bg-black/50 overflow-y-auto">
        <div className="w-full p-2 sm:p-4 md:p-4">
          <div
            data-lenis-prevent
            className={`w-full rounded-none sm:rounded-xl lg:max-w-4xl mx-auto overflow-hidden ${
              theme === "dark" ? "bg-card" : "bg-white"
            }`}
          >
            <div className="p-2 sm:p-4">
              <div
                className={`flex items-center justify-between border-b ${
                  theme === "dark"
                    ? "border-border bg-card"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F0B90B]/20 flex items-center justify-center">
                    <Send className="w-5 h-5 text-primary" />
                  </div>

                  <div>
                    <h3
                      className={`text-lg font-semibold ${
                        theme === "dark" ? "text-foreground" : "text-gray-900"
                      }`}
                    >
                      Transfer{" "}
                      {removeTrailingToken(activeAsset?.token?.name ?? "")}
                    </h3>

                    <p
                      className={`text-sm ${
                        theme === "dark"
                          ? "text-muted-foreground"
                          : "text-gray-500"
                      }`}
                    >
                      Send to another BUYFIAT user
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-2 sm:p-4">
                <WalletSummaryCard wallet={wallet} />

                <SelectedAssetCard asset={activeAsset} />

                <AssetSelector
                  assets={wallet?.assets ?? []}
                  value={activeAsset}
                  onChange={(asset) => {
                    setActiveAsset(asset);
                  }}
                  label="Select Asset"
                  placeholder="Search token, currency or network..."
                />

                <div
                  className={`p-4 rounded-lg ${
                    theme === "dark" ? "bg-muted" : "bg-gray-100"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Available Balance
                  </p>

                  <p
                    className={`text-xl font-bold font-mono ${
                      isInsufficient ? "text-red-500" : "text-emerald-500"
                    }`}
                  >
                    {activeAsset?.token?.symbol}{" "}
                    {formatEvmHumanBalance(
                      availableBalance,
                      activeAsset?.token?.isNative as boolean,
                    )}
                  </p>

                  {isInsufficient && (
                    <p className="text-xs text-red-500 mt-1">
                      Insufficient balance for this transfer
                    </p>
                  )}
                </div>

                {/* Recipient */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Recipient Email or UID
                  </label>

                  <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                      selectingRecipientRef.current = false;
                      setQuery(e.target.value);
                      setSelectedRecipient(null);
                    }}
                    placeholder="Enter email, UID or wallet address"
                    aria-invalid={recipientInputInvalid}
                    className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none h-14 ${
                      recipientInputInvalid
                        ? "border-red-500 focus:border-red-500"
                        : "focus:border-primary"
                    } ${
                      theme === "dark"
                        ? "bg-muted text-foreground placeholder:text-muted-foreground"
                        : "bg-white text-gray-900 placeholder-gray-400"
                    } ${
                      recipientInputInvalid
                        ? "border-red-500"
                        : theme === "dark"
                          ? "border-border"
                          : "border-gray-200"
                    }`}
                  />

                  {recipientInputInvalid && (
                    <p className="text-xs text-red-500 mt-1">
                      {recipientError}
                    </p>
                  )}
                </div>

                {(results.length > 0 || isSearching) && (
                  <div
                    data-lenis-prevent
                    className={`mt-2 border rounded-lg overflow-y-auto max-h-60 ${
                      theme === "dark"
                        ? "border-border bg-card"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    {isSearching ? (
                      <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Searching...
                      </div>
                    ) : (
                      results.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => {
                            selectingRecipientRef.current = true;

                            setSelectedRecipient(user);

                            const recipientValue =
                              user.email?.trim() ||
                              user.address?.trim() ||
                              user.uid?.trim() ||
                              "";

                            setQuery(recipientValue);
                            setResults([]);
                            setIsSearching(false);

                            // Allow searching again if the user edits the field later.
                            setTimeout(() => {
                              selectingRecipientRef.current = false;
                            }, 0);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-muted transition"
                        >
                          <div className="font-medium uppercase">
                            {user.name}
                          </div>

                          <div className="text-xs text-muted-foreground">
                            {user.email || user.address || user.id}
                          </div>

                          <div className="font-bold uppercase text-emerald-500 text-xl">
                            {user.uid}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}

                {/* Amount */}
                <div>
                  <label
                    className={`block text-sm mb-2 ${
                      theme === "dark"
                        ? "text-muted-foreground"
                        : "text-gray-500"
                    }`}
                  >
                    Amount
                  </label>

                  <div className="relative">
                    <input
                      type="number"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="any"
                      aria-invalid={amountInputInvalid}
                      className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none h-14 ${
                        amountInputInvalid
                          ? "border-red-500 focus:border-red-500"
                          : "focus:border-primary"
                      } ${
                        theme === "dark"
                          ? "bg-muted text-foreground placeholder:text-muted-foreground"
                          : "bg-white text-gray-900 placeholder-gray-400"
                      } ${
                        amountInputInvalid
                          ? "border-red-500"
                          : theme === "dark"
                            ? "border-border"
                            : "border-gray-200"
                      }`}
                    />
                  </div>

                  <div className="flex items-center justify-between gap-4 min-h-6 mt-2">
                    <div className="min-w-0">
                      {amountInputInvalid && (
                        <p className="text-xs text-red-500 mt-1">
                          {amountError}
                        </p>
                      )}
                    </div>

                    {quote && quote?.usdEquivalent && (
                      <div
                        className={`ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs ${
                          theme === "dark"
                            ? "bg-emerald-500/10 text-emerald-400"
                            : "bg-emerald-50 text-emerald-600"
                        }`}
                      >
                        <span className="font-medium">USDT equivalent</span>
                        <span className="font-bold">{quote.usdEquivalent}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quote */}
                {loading ? (
                  <div
                    className={`p-4 rounded-lg border ${
                      theme === "dark"
                        ? "border-border bg-muted"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculating quote...
                    </p>
                  </div>
                ) : quote && !isInsufficient ? (
                  <div
                    className={`p-4 rounded-lg border ${
                      theme === "dark"
                        ? "border-border bg-muted"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex justify-between text-xs text-muted-foreground mb-2">
                      <span>Fee Breakdown</span>

                      {quote.fee?.enabled ? (
                        <span className="text-emerald-500">Fee applied</span>
                      ) : (
                        <span>No fee</span>
                      )}
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span>Amount</span>

                        <span>
                          {formatEvmHumanBalance(
                            transferAmount,
                            activeAsset?.token?.isNative as boolean,
                          )}{" "}
                          {normalizedCurrency}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span>Fee</span>

                        <span className="text-amber-500">
                          {formatEvmHumanBalance(
                            quote.fee?.feeAmount,
                            activeAsset?.token?.isNative as boolean,
                          )}{" "}
                          {normalizedCurrency}
                        </span>
                      </div>

                      <div className="flex justify-between font-semibold">
                        <span>Total Debit</span>

                        <span>
                          {formatEvmHumanBalance(
                            quote.settlement?.totalDebit,
                            activeAsset?.token?.isNative as boolean,
                          )}{" "}
                          {normalizedCurrency}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Validation Error */}
                {validationError && (
                  <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />

                      <div>
                        <p className="text-sm font-medium text-red-500">
                          Transfer cannot continue
                        </p>

                        <p className="text-xs text-red-500/90 mt-1">
                          {validationError}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transfer Button */}
                <button
                  onClick={handleClick}
                  disabled={!canContinue}
                  className="w-full py-4 rounded-lg bg-[#F0B90B] text-black font-semibold hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    "Transfer"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {wallet?.isSystem ? (
        <AdminVerificationDialog
          open={verificationOpen}
          onOpenChange={setVerificationOpen}
          systemType={wallet?.systemType}
          onSuccess={executeTransfer}
        />
      ) : (
        <UserVerificationDialog
          open={verificationOpen}
          onOpenChange={setVerificationOpen}
          loading={false}
          onVerify={executeTransfer}
        />
      )}

      <TransactionSuccessDialog
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Transfer Successful"
        message={`Your ${successData?.asset} transfer to ${successData?.recipient} was completed successfully.`}
      />
    </div>
  );
};

export default TransferContainer;
